import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { SWRConfig } from "swr";
import { swrDefaults } from "./lib/swr";
import { Toaster } from "./components/ui/sonner";

// Contexts
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { PlannerProvider } from "./contexts/PlannerContext";
import { ExpedientesProvider } from "./contexts/ExpedientesContext";

// Components — eager (used in first paint or auth gates)
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import SoapDiagnosticsPanel from "./components/SoapDiagnosticsPanel";

// Auth pages — eager so login flow has no flicker.
import LoginPage from "./pages/LoginPage";

// ── All other pages lazy-loaded (Vero Feb 20 2026 · Performance Fase B) ──
// React.lazy + Suspense splits the bundle by route. First paint downloads
// only Navbar + LoginPage + HomePage; the rest streams on demand when
// the user navigates. Estimated bundle saving on initial load: ~70%.
const HomePage = lazy(() => import("./pages/HomePage"));
const FlightsPage = lazy(() => import("./pages/FlightsPage"));
const HotelsPage = lazy(() => import("./pages/HotelsPage"));
const ToursPage = lazy(() => import("./pages/ToursPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const PassengersPage = lazy(() => import("./pages/PassengersPage"));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const BookingConfirmationPage = lazy(() => import("./pages/BookingConfirmationPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage"));
const ActivateAccountPage = lazy(() => import("./pages/ActivateAccountPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const AdminReservasPage = lazy(() => import("./pages/AdminReservasPage"));
const AdminReembolsosPage = lazy(() => import("./pages/AdminReembolsosPage"));
const AdminAgenciasPage = lazy(() => import("./pages/AdminAgenciasPage"));
const AdminUsuariosPage = lazy(() => import("./pages/AdminUsuariosPage"));
const AdminAgenciesPage = lazy(() => import("./pages/AdminAgenciesPage"));
const TourDetailPage = lazy(() => import("./pages/TourDetailPage"));
const TourOverviewPage = lazy(() => import("./pages/TourOverviewPage"));
const MyBookingDetailPage = lazy(() => import("./pages/MyBookingDetailPage"));
const AgencyDashboardPage = lazy(() => import("./pages/AgencyDashboardPage"));
const AgencyProfilePage = lazy(() => import("./pages/AgencyProfilePage"));
const QuoteFinalizePage = lazy(() => import("./pages/QuoteFinalizePage"));
const PrereservationPaymentPage = lazy(() => import("./pages/PrereservationPaymentPage"));
const HotelDetailPage = lazy(() => import("./pages/HotelDetailPage"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const AdminAuditLogPage = lazy(() => import("./pages/AdminAuditLogPage"));
const BackOfficePage = lazy(() => import("./pages/BackOfficePage"));
const PublicQuotePage = lazy(() => import("./pages/PublicQuotePage"));
const MockResultsPage = lazy(() => import("./pages/MockResultsPage"));
const ActivitiesPage = lazy(() => import("./pages/ActivitiesPage"));
const ActivityDetailPage = lazy(() => import("./pages/ActivityDetailPage"));
const ActivitiesCheckoutPage = lazy(() => import("./pages/ActivitiesCheckoutPage"));
const PublicMultiQuotePage = lazy(() => import("./pages/PublicMultiQuotePage"));
const PJRCatalogoPage = lazy(() => import("./pages/admin/PJRCatalogoPage"));
const PJROrdenPorDestinoPage = lazy(() => import("./pages/admin/PJROrdenPorDestinoPage"));
const PJRMarketingPage = lazy(() => import("./pages/admin/PJRMarketingPage"));
const PJRSaludPage = lazy(() => import("./pages/admin/PJRSaludPage"));
const PJRFormularioPage = lazy(() => import("./pages/admin/PJRFormularioPage"));
const PJRHotelesPage = lazy(() => import("./pages/admin/PJRHotelesPage"));
const PJRImagenesPage = lazy(() => import("./pages/admin/PJRImagenesPage"));
const PJRPoliticasPage = lazy(() => import("./pages/admin/PJRPoliticasPage"));
const PJRCategoriasPage = lazy(() => import("./pages/admin/PJRCategoriasPage"));
const PJRTagsPage = lazy(() => import("./pages/admin/PJRTagsPage"));
const EMTagsPage = lazy(() => import("./pages/admin/EMTagsPage"));
const EMCertificationPage = lazy(() => import("./pages/admin/EMCertificationPage"));
const PJRCalendarioPage = lazy(() => import("./pages/admin/PJRCalendarioPage"));
const PJRCalendariosPage = lazy(() => import("./pages/admin/PJRCalendariosPage"));
const PJRProveedoresPage = lazy(() => import("./pages/admin/PJRProveedoresPage"));
const PJRSupplierBookingsPage = lazy(() => import("./pages/admin/PJRSupplierBookingsPage"));
const PJRRolesPage = lazy(() => import("./pages/admin/PJRRolesPage"));
const PJRUsuariosPage = lazy(() => import("./pages/admin/PJRUsuariosPage"));
import {
  SupplierAuthProvider,
  SupplierLoginPage,
  SupplierLayout,
  SupplierDashboardPage,
  SupplierBookingsPage,
} from "./pages/supplier/SupplierPortalShell";
const SupplierBookingDetailPage = lazy(() => import("./pages/supplier/SupplierBookingDetailPage"));
const PJRPublicProductPage = lazy(() => import("./pages/PJRPublicProductPage"));
const PJRCatalogListPage = lazy(() => import("./pages/PJRCatalogListPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const PJRCotizarPage = lazy(() => import("./pages/PJRCotizarPage"));
const PlannerPage = lazy(() => import("./pages/PlannerPage"));
const ExpedientesPage = lazy(() => import("./pages/ExpedientesPage"));
const PJRPublicQuotePage = lazy(() => import("./pages/PJRPublicQuotePage"));
const PublicPlannerPage = lazy(() => import("./pages/PublicPlannerPage"));

// Protected Route Component
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Layout with Navbar and Footer
function MainLayout({ children }) {
  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-[68px]">{children}</main>
      <Footer />
      <SoapDiagnosticsPanel />
    </>
  );
}

// Auth gate: the whole portal is private — anonymous visitors land on /login.
// Public exceptions are handled in AppRouter (quotes, auth callbacks).
function AuthGate({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

// Auth layout without Navbar/Footer
function AuthLayout({ children }) {
  return <main>{children}</main>;
}

// Suspense fallback shown while a lazy route bundle loads.
// Mirrors AuthGate / ProtectedRoute spinner for visual continuity.
function PageFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="page-loader">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// App Router Component
function AppRouter() {
  const location = useLocation();

  // ── Visible scrollbar in admin routes (Vero Feb 20 2026) ──
  // Toggle a `data-pjr-admin` attribute on <html> so the index.css
  // selector lights up the prominent 14px scrollbar only when an
  // operator is inside /admin/*. Marketing pages keep the slim 8px bar.
  useEffect(() => {
    const isAdmin = location.pathname.startsWith('/admin');
    if (isAdmin) document.documentElement.setAttribute('data-pjr-admin', 'true');
    else document.documentElement.removeAttribute('data-pjr-admin');
  }, [location.pathname]);

  // Check for OAuth callback - process session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  // ── /suppliers/* convenience alias ──────────────────────────────────
  // The internal portal lives under `/supplier/*` (singular), but the
  // public URL `beta.pjrtravel.com/suppliers` reads more naturally for
  // operators. Forward any /suppliers prefix to /supplier so links from
  // emails / documents keep working without a code-wide rename.
  if (location.pathname === '/suppliers' || location.pathname.startsWith('/suppliers/')) {
    const target = location.pathname.replace(/^\/suppliers/, '/supplier') + location.search;
    return <Navigate to={target || '/supplier/login'} replace />;
  }

  // ── Host-based routing for the suppliers subdomain ──────────────────
  // When users land on suppliers.pjrtravel.com (or its preview equivalents),
  // we silently force them into the /supplier/* portal regardless of the
  // path they typed. This keeps the agency CMS invisible to operators.
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isSuppliersHost = host.startsWith('suppliers.');
  if (isSuppliersHost && !location.pathname.startsWith('/supplier')) {
    return <Navigate to="/supplier/login" replace />;
  }

  // Public quote viewer — no navbar/footer chrome, no auth.
  if (location.pathname.startsWith('/quote/')) {
    return (
      <Suspense fallback={<PageFallback />}><Routes>
        <Route path="/quote/multi/:token" element={<PublicMultiQuotePage />} />
        <Route path="/quote/:token" element={<PublicQuotePage />} />
      </Routes></Suspense>
    );
  }

  // Formal expediente quote (/q/:token) — client-facing, PUBLIC (no auth):
  // the end customer receives this link from the agent and must be able
  // to view/print/accept it without a B2B account.
  if (location.pathname.startsWith('/q/')) {
    return (
      <Suspense fallback={<PageFallback />}><Routes>
        <Route path="/q/:token" element={<PJRPublicQuotePage />} />
      </Routes></Suspense>
    );
  }

  // Public Planner share link — read-only traveler view, no auth.
  if (location.pathname.startsWith('/p/')) {
    return (
      <Suspense fallback={<PageFallback />}><Routes>
        <Route path="/p/:token" element={<PublicPlannerPage />} />
      </Routes></Suspense>
    );
  }

  // PJR public product detail — public pages, but they MUST use the same
  // Navbar/Footer chrome as the rest of the app so users feel at home and
  // the cart/planner badges stay visible. No AuthGate (public access).
  if (location.pathname.startsWith('/pjr/') || location.pathname === '/pjr-select' || location.pathname === '/grupos-incluido') {
    return (
      <MainLayout>
        <Suspense fallback={<PageFallback />}><Routes>
          <Route path="/pjr/:slug/cotizar" element={<PJRCotizarPage />} />
          <Route path="/pjr/:slug" element={<PJRPublicProductPage />} />
          <Route path="/pjr-select" element={<PJRCatalogListPage variant="pjr_select" />} />
          <Route path="/grupos-incluido" element={<PJRCatalogListPage variant="grupos" />} />
        </Routes></Suspense>
      </MainLayout>
    );
  }

  // Legal pages — public, standard chrome (navbar + footer), no auth gate.
  if (location.pathname === '/privacidad' || location.pathname === '/privacy-policy') {
    return (
      <MainLayout>
        <Suspense fallback={<PageFallback />}><Routes>
          <Route path="/privacidad" element={<PrivacyPolicyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        </Routes></Suspense>
      </MainLayout>
    );
  }

  // Suppliers portal — fully isolated branch. No navbar, no PJR auth, its
  // own auth context (cookie `supplier_token`).
  if (location.pathname.startsWith('/supplier')) {
    return (
      <SupplierAuthProvider>
        <Suspense fallback={<PageFallback />}><Routes>
          <Route path="/supplier/login" element={<SupplierLoginPage />} />
          <Route path="/supplier" element={<SupplierLayout />}>
            <Route index element={<SupplierDashboardPage />} />
            <Route path="bookings" element={<SupplierBookingsPage />} />
            <Route path="bookings/:requestId" element={<SupplierBookingDetailPage />} />
          </Route>
        </Routes></Suspense>
      </SupplierAuthProvider>
    );
  }

  // Auth pages don't have navbar/footer
  const isAuthPage = ['/login', '/register', '/auth/callback', '/activate', '/forgot-password', '/reset-password'].includes(location.pathname);

  if (isAuthPage) {
    return (
      <AuthLayout>
        <Suspense fallback={<PageFallback />}><Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/activate" element={<ActivateAccountPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes></Suspense>
      </AuthLayout>
    );
  }

  // PJR CMS pages have their own chrome (topbar + sidebar from pjr-cms.css);
  // skip the public Navbar/Footer to avoid double headers.
  if (location.pathname.startsWith('/admin/pjr') || location.pathname.startsWith('/admin/europamundo')) {
    return (
      <AuthGate>
        <Suspense fallback={<PageFallback />}><Routes>
          <Route path="/admin/pjr/catalogo" element={<ProtectedRoute adminOnly><PJRCatalogoPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/orden-por-destino" element={<ProtectedRoute adminOnly><PJROrdenPorDestinoPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/marketing" element={<ProtectedRoute adminOnly><PJRMarketingPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/salud" element={<ProtectedRoute adminOnly><PJRSaludPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/productos/nuevo" element={<ProtectedRoute adminOnly><PJRFormularioPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/productos/:productId/editar" element={<ProtectedRoute adminOnly><PJRFormularioPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/hoteles" element={<ProtectedRoute adminOnly><PJRHotelesPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/imagenes" element={<ProtectedRoute adminOnly><PJRImagenesPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/politicas" element={<ProtectedRoute adminOnly><PJRPoliticasPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/categorias" element={<ProtectedRoute adminOnly><PJRCategoriasPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/tags" element={<ProtectedRoute adminOnly><PJRTagsPage /></ProtectedRoute>} />
          <Route path="/admin/europamundo/etiquetas" element={<ProtectedRoute adminOnly><EMTagsPage /></ProtectedRoute>} />
          <Route path="/admin/europamundo/certificacion" element={<ProtectedRoute adminOnly><EMCertificationPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/calendarios" element={<ProtectedRoute adminOnly><PJRCalendariosPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/productos/:productId/calendario" element={<ProtectedRoute adminOnly><PJRCalendarioPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/proveedores" element={<ProtectedRoute adminOnly><PJRProveedoresPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/supplier-bookings" element={<ProtectedRoute adminOnly><PJRSupplierBookingsPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/roles" element={<ProtectedRoute adminOnly><PJRRolesPage /></ProtectedRoute>} />
          <Route path="/admin/pjr/usuarios" element={<ProtectedRoute adminOnly><PJRUsuariosPage /></ProtectedRoute>} />
        </Routes></Suspense>
      </AuthGate>
    );
  }

  // New Back-Office Dashboard (Design System #05) — has its own topbar
  // + sidebar so it skips the public Navbar/Footer chrome.
  if (location.pathname === '/admin') {
    return (
      <AuthGate>
        <ProtectedRoute adminOnly>
          <AdminDashboardPage />
        </ProtectedRoute>
      </AuthGate>
    );
  }

  // Back-Office screens 2/3/4 (Reservas, Agencias, Usuarios) — same shell.
  if (location.pathname === '/admin/reservas'
      || location.pathname === '/admin/reembolsos'
      || location.pathname === '/admin/agencias'
      || location.pathname === '/admin/usuarios') {
    return (
      <AuthGate>
        <ProtectedRoute adminOnly>
          <Suspense fallback={<PageFallback />}><Routes>
            <Route path="/admin/reservas" element={<AdminReservasPage />} />
            <Route path="/admin/reembolsos" element={<AdminReembolsosPage />} />
            <Route path="/admin/agencias" element={<AdminAgenciasPage />} />
            <Route path="/admin/usuarios" element={<AdminUsuariosPage />} />
          </Routes></Suspense>
        </ProtectedRoute>
      </AuthGate>
    );
  }

  // Legacy routes — redirect to the canonical Spanish names so we have
  // a single source of truth for the new Back-Office.
  const redirectMap = {
    '/admin/back-office': '/admin/reservas',
    '/admin/agencies': '/admin/agencias',
    '/admin/users': '/admin/usuarios',
  };
  if (redirectMap[location.pathname]) {
    return <Navigate to={redirectMap[location.pathname]} replace />;
  }

  return (
    <AuthGate>
      <MainLayout>
        <Suspense fallback={<PageFallback />}><Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/flights" element={<FlightsPage />} />
        <Route path="/hotels" element={<HotelsPage />} />
        <Route path="/hotels/detail/:universalId" element={<HotelDetailPage />} />
        <Route path="/tours" element={<ToursPage />} />
        <Route path="/tours/:tourId" element={<TourOverviewPage />} />
        <Route path="/tours/:tourId/book" element={<TourDetailPage />} />
        {/* Civitatis Activities module */}
        <Route path="/activities" element={<ActivitiesPage />} />
        <Route path="/activities/checkout" element={<ProtectedRoute><ActivitiesCheckoutPage /></ProtectedRoute>} />
        <Route path="/activities/:id" element={<ActivityDetailPage />} />
        {/* Phase-1 mock results pages — wired up to the home search tabs
            until their respective inventory feeds are connected. */}
        <Route path="/pjr-select" element={<PJRCatalogListPage variant="pjr_select" />} />
        <Route path="/grupos-incluido" element={<PJRCatalogListPage variant="grupos" />} />
        <Route path="/traslados" element={<MockResultsPage variant="traslados" />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/passengers" element={<ProtectedRoute><PassengersPage /></ProtectedRoute>} />
        <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
        <Route path="/planner" element={<ExpedientesPage />} />
        <Route path="/planner-legacy" element={<PlannerPage />} />
        <Route path="/q/:token" element={<PJRPublicQuotePage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/pay/:bookingId" element={<ProtectedRoute><PrereservationPaymentPage /></ProtectedRoute>} />
        <Route path="/booking-confirmation" element={<BookingConfirmationPage />} />
        <Route path="/my-bookings" element={<Navigate to="/dashboard?tab=bookings" replace />} />
        <Route path="/agency/profile" element={<ProtectedRoute><AgencyProfilePage /></ProtectedRoute>} />
        <Route path="/my-bookings/:booking_id" element={<ProtectedRoute><MyBookingDetailPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><AgencyDashboardPage /></ProtectedRoute>} />
        <Route path="/quotes/:quote_id/finalize" element={<ProtectedRoute><QuoteFinalizePage /></ProtectedRoute>} />
        <Route path="/my-quotes" element={<Navigate to="/dashboard" replace />} />
        <Route path="/my-agency" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/agencies"
          element={
            <ProtectedRoute adminOnly>
              <AdminAgenciesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute adminOnly>
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-log"
          element={
            <ProtectedRoute adminOnly>
              <AdminAuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/back-office"
          element={
            <ProtectedRoute adminOnly>
              <BackOfficePage />
            </ProtectedRoute>
          }
        />
        {/* PJR own-products CMS — admin/super_admin only */}
        <Route path="/admin/pjr/catalogo" element={<ProtectedRoute adminOnly><PJRCatalogoPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/orden-por-destino" element={<ProtectedRoute adminOnly><PJROrdenPorDestinoPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/marketing" element={<ProtectedRoute adminOnly><PJRMarketingPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/productos/nuevo" element={<ProtectedRoute adminOnly><PJRFormularioPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/productos/:productId/editar" element={<ProtectedRoute adminOnly><PJRFormularioPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/hoteles" element={<ProtectedRoute adminOnly><PJRHotelesPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/imagenes" element={<ProtectedRoute adminOnly><PJRImagenesPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/politicas" element={<ProtectedRoute adminOnly><PJRPoliticasPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/categorias" element={<ProtectedRoute adminOnly><PJRCategoriasPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/tags" element={<ProtectedRoute adminOnly><PJRTagsPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/calendarios" element={<ProtectedRoute adminOnly><PJRCalendariosPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/productos/:productId/calendario" element={<ProtectedRoute adminOnly><PJRCalendarioPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/proveedores" element={<ProtectedRoute adminOnly><PJRProveedoresPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/supplier-bookings" element={<ProtectedRoute adminOnly><PJRSupplierBookingsPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/roles" element={<ProtectedRoute adminOnly><PJRRolesPage /></ProtectedRoute>} />
        <Route path="/admin/pjr/usuarios" element={<ProtectedRoute adminOnly><PJRUsuariosPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes></Suspense>
    </MainLayout>
    </AuthGate>
  );
}

function App() {
  return (
    <div className="App">
      <SWRConfig value={swrDefaults}>
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <PlannerProvider>
                <ExpedientesProvider>
                  <AppRouter />
                  <Toaster position="top-right" />
                </ExpedientesProvider>
              </PlannerProvider>
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
      </SWRConfig>
    </div>
  );
}

export default App;
